import React from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Users, DollarSign, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, Cell } from 'recharts';
import { formatCurrency } from '../../utils/calculations';
const REVENUE_DATA = [{
  month: 'Jan',
  revenue: 12000000,
  prediction: 12500000
}, {
  month: 'Feb',
  revenue: 13500000,
  prediction: 13800000
}, {
  month: 'Mar',
  revenue: 12800000,
  prediction: 13200000
}, {
  month: 'Apr',
  revenue: 14200000,
  prediction: 14500000
}, {
  month: 'May',
  revenue: 15100000,
  prediction: 15500000
}, {
  month: 'Jun',
  revenue: 16500000,
  prediction: 16800000
}, {
  month: 'Jul',
  revenue: null,
  prediction: 17500000
}, {
  month: 'Aug',
  revenue: null,
  prediction: 18200000
}];
const RISK_DATA = [{
  name: 'Low Risk',
  value: 65,
  color: '#10B981'
}, {
  name: 'Medium Risk',
  value: 25,
  color: '#F59E0B'
}, {
  name: 'High Risk',
  value: 10,
  color: '#EF4444'
}];
export function PlatformInsights() {
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4633">
      <Sidebar data-id="element-4634" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4635">
        <DashboardHeader data-id="element-4636" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4637">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4638">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 lg:mb-8 gap-4" data-id="element-4639">
              <div className="min-w-0" data-id="element-4640">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3" data-id="element-4641">
                  <Brain className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" data-id="element-4642" />
                  <span className="truncate" data-id="element-4643">AI Platform Intelligence</span>
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-4644">
                  Real-time predictive analytics and market insights
                </p>
              </div>
              <span className="px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-bold flex items-center gap-2 whitespace-nowrap flex-shrink-0" data-id="element-4645">
                <Activity className="w-4 h-4 flex-shrink-0" data-id="element-4646" /> Live Analysis
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8" data-id="element-4647">
              {[{
              icon: DollarSign,
              label: 'Predicted Revenue (Q4)',
              value: formatCurrency(45000000),
              change: '+12.5%',
              color: 'blue'
            }, {
              icon: Users,
              label: 'Tenant Retention',
              value: '94.8%',
              change: '+8.2%',
              color: 'purple'
            }, {
              icon: TrendingUp,
              label: 'Avg. Funder Yield',
              value: '26.4%',
              change: '+15%',
              color: 'green'
            }, {
              icon: Activity,
              label: 'Default Risk',
              value: '1.8%',
              change: '-2.1%',
              color: 'red'
            }].map((metric, idx) => <div key={idx} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-4648">
                  <div className="flex justify-between items-start mb-4" data-id="element-4649">
                    <div className={`p-2 bg-${metric.color}-50 dark:bg-${metric.color}-900/30 rounded-lg`} data-id="element-4650">
                      <metric.icon className={`w-6 h-6 text-${metric.color}-600 dark:text-${metric.color}-400`} data-id="element-4651" />
                    </div>
                    <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full whitespace-nowrap" data-id="element-4652">
                      {metric.change}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-4653">
                    {metric.label}
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-4654">
                    {metric.value}
                  </h3>
                </div>)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-4655">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-4656">
                <h3 className="font-bold text-gray-900 dark:text-white mb-6 truncate" data-id="element-4657">
                  Revenue Forecast (AI Model)
                </h3>
                <div className="h-80" data-id="element-4658">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-4659">
                    <AreaChart data={REVENUE_DATA} data-id="element-4660">
                      <defs data-id="element-4661">
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1" data-id="element-4662">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} data-id="element-4663" />
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} data-id="element-4664" />
                        </linearGradient>
                        <linearGradient id="colorPrediction" x1="0" y1="0" x2="0" y2="1" data-id="element-4665">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.1} data-id="element-4666" />
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-4667" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-4668" />
                      <XAxis dataKey="month" data-id="element-4669" />
                      <YAxis tickFormatter={val => `${val / 1000000}M`} data-id="element-4670" />
                      <Tooltip formatter={(val: number) => formatCurrency(val)} data-id="element-4671" />
                      <Legend data-id="element-4672" />
                      <Area type="monotone" dataKey="revenue" name="Actual Revenue" stroke="#3B82F6" fill="url(#colorRevenue)" strokeWidth={2} data-id="element-4673" />
                      <Area type="monotone" dataKey="prediction" name="AI Prediction" stroke="#8B5CF6" fill="url(#colorPrediction)" strokeDasharray="5 5" strokeWidth={2} data-id="element-4674" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-4675">
                <h3 className="font-bold text-gray-900 dark:text-white mb-6 truncate" data-id="element-4676">
                  Risk Distribution Analysis
                </h3>
                <div className="h-80" data-id="element-4677">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-4678">
                    <BarChart data={RISK_DATA} layout="vertical" data-id="element-4679">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} data-id="element-4680" />
                      <XAxis type="number" data-id="element-4681" />
                      <YAxis dataKey="name" type="category" width={100} data-id="element-4682" />
                      <Tooltip data-id="element-4683" />
                      <Bar dataKey="value" fill="#8884d8" radius={[0, 4, 4, 0]} data-id="element-4684">
                        {RISK_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4685" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}